import { CFC_CheckAPIVersion } from '@rabbit/bizproc/core';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

/** Global API management on the FE. Don't call this directly, instead use useSageAPI and useOliveAPI */
export function useAPI() {
  const [requestId, setRequestId] = useState<string>(uuidv4());
  const [apiVersion, setApiVersion] = useState<string | null>(null);
  const isReady = !!apiVersion;

  /**
   * Ids this hook instance currently has in flight, so a double submit (say a
   * double clicked save button) doesn't reach the API twice.
   *
   * This used to be a single `currentRequestId` slot in sessionStorage, which
   * wedged the app: the slot was shared by every useAPI instance in the tab and
   * survived reloads, it was only cleared on the success path, and the duplicate
   * check threw before the try/finally that rotates the id. So one failed call
   * left a key behind that matched a requestId which could no longer rotate, and
   * every later call threw "Request already processed" until the tab was closed.
   */
  const inFlightIds = useRef<Set<string>>(new Set());

  // On hook load, try to get the api version from session storage.
  // All successful CF calls should include that info and store it in session storage.
  // But if for some reason it hasn't been stored yet, ask the API for it
  useEffect(() => {
    const storedApiVersion = sessionStorage.getItem('apiVersion');
    if (
      storedApiVersion &&
      storedApiVersion !== 'null' &&
      storedApiVersion !== 'undefined'
    ) {
      setApiVersion(storedApiVersion);
    } else {
      console.log('Fetching api version');
      // Get the API version
      CFC_CheckAPIVersion.call({ requestId, apiVersion: null })
        .then((version) => {
          if (version.ok) {
            setApiVersion(version.data.apiVersion);
            sessionStorage.setItem('apiVersion', version.data.apiVersion);
            console.log('API version set to', version.data.apiVersion);
          }
        })
        .catch((e) => {
          console.error('Failed to get API version:', e);
        });
    }
  }, []);

  // console.log('requestId', requestId);

  /** Generic API call wrapper */
  async function callAPI(
    fn: (...args: any) => Promise<any>,
    params: { [key: string]: any }
  ) {
    // If there's no api version, throw an error right away
    if (!apiVersion) {
      throw new Error('API version not available on client, aborting...');
    }

    // The same id being in flight means this is a repeat submit, not a new call
    if (inFlightIds.current.has(requestId)) {
      console.log('Discarding duplicate request with requestId:', requestId);
      throw new Error('Request already processed');
    }

    // Pin the id for this call so the cleanup below can't be confused by a
    // rotation that happened while we were awaiting
    const callId = requestId;
    inFlightIds.current.add(callId);

    // call the function with the provided params + requestID
    try {
      const result = await fn({ requestId: callId, apiVersion, ...params });

      return result.data;
    } catch (err: any) {
      if (err?.message?.includes('API_VERSION_MISMATCH')) {
        toast.error(
          'App version mismatch detected, please wait while we reload the page...'
        );
        // remove the app config from local storage so it gets refreshed on next load
        localStorage.removeItem('APPCONFIG');

        // reload the page after 2 seconds
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else throw err;
    } finally {
      // Release the id and regenerate it whether the call resolved or threw, so
      // a failed request can never block the retry
      inFlightIds.current.delete(callId);
      setRequestId(uuidv4());
    }
  }
  return { requestId, apiVersion, isReady, callAPI };
}
